// Vista global de "Gestió laboral -> Empleats" (CURRENT_TASK.md, punt 19):
// amb "Empleat = Tots" mostra un resum de situació de tota l'Empresa
// (filtres, taula/llistat, Absències actuals/properes, Baixes obertes/
// recents i comptadors), sense cap calendari per defecte. En seleccionar
// un Empleat concret, reutilitza exactament el mateix component de gestió
// individual que "Empleats -> Gestió" (GestioEmpleat.js): no és un
// subsistema diferent, és la mateixa lògica amb una altra porta d'entrada.

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useUserContext } from "../../utils/UserContext";
import { tincPermis } from "../../utils/Permisos";
import { fetchAutenticat, SessioCaducadaError } from "../../utils/auth";
import { formatDate, formatNomPersona } from "../../utils/Utilitats";
import { API_URL } from "../../utils/config";
import GestioEmpleat from "./GestioEmpleat";
import { formatMinuts } from "./ControlHorariEmpleat";
import AjudaModal from "./AjudaModal";
import "../dashboard_v2.css";
import "./gestiolaboralempleats.css";

function dataISOLocal(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dia = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dia}`;
}

function dataPerDefecteDesde() {
  const d = new Date();
  d.setDate(1);
  return dataISOLocal(d);
}

function dataPerDefecteFinsa() {
  return dataISOLocal(new Date());
}

const ESTAT_OPCIONS = [
  { valor: "PENDENT", nom: "Pendent" },
  { valor: "JUSTIFICADA", nom: "Justificada" },
  { valor: "NO_JUSTIFICADA", nom: "No justificada" },
];

function AjudaGestioLaboralEmpleats({ onTancar }) {
  return (
    <AjudaModal
      id="gl-empleats-ajuda-title"
      titol="Com funciona Empleats de Gestió laboral"
      lead="Resum de situació de tots els empleats de l'empresa activa: baixes, absències, pendents i hores del període."
      onTancar={onTancar}
    >
      <section className="dv2-help-section">
        <h4>Per a què serveix</h4>
        <p>
          Mostra d'un cop d'ull qui està de baixa, qui té absència avui, qui té pendents de justificar i el còmput
          d'hores del període filtrat. No és el calendari de l'empresa: això està a <strong>Calendari laboral</strong>.
        </p>
      </section>
      <section className="dv2-help-section">
        <h4>Filtres</h4>
        <ul>
          <li>
            <strong>Des de / Fins a</strong>: per defecte, del dia 1 del mes actual fins avui. Canviar-los recarrega el
            resum i les hores.
          </li>
          <li>
            <strong>Empleat</strong>: «Tots» deixa la llista; triar-ne un obre la seva Gestió (Horari, Absències,
            Baixes…).
          </li>
          <li>
            <strong>Tipus / Estat / Actius / cerca</strong> filtren la taula sense tornar a demanar el servidor.
          </li>
        </ul>
      </section>
      <section className="dv2-help-section">
        <h4>Obrir un empleat</h4>
        <p>
          Doble clic a la fila o el botó <strong>Gestió</strong>. Allà cada pestanya té la seva ajuda amb el <strong>?</strong>{" "}
          de la capçalera.
        </p>
      </section>
    </AjudaModal>
  );
}

function GestioLaboralEmpleats() {
  const { rolUsuari, empresaSeleccionada } = useUserContext();
  const potAccedir = tincPermis(rolUsuari, "ACCES", "EMPLEATS");

  const [resum, setResum] = useState(null);
  const [tipusOptions, setTipusOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [mostraAjuda, setMostraAjuda] = useState(false);

  const [mostrarActius, setMostrarActius] = useState(true);
  const [desde, setDesde] = useState(dataPerDefecteDesde());
  const [finsa, setFinsa] = useState(dataPerDefecteFinsa());
  const [filtreTipus, setFiltreTipus] = useState("");
  const [filtreEstat, setFiltreEstat] = useState("");
  const [filtreText, setFiltreText] = useState("");

  const [empleatSeleccionat, setEmpleatSeleccionat] = useState(null);

  const idEmpresa = empresaSeleccionada?.IdEmpresa;

  const carregarTipus = useCallback(async () => {
    if (!idEmpresa) return;
    try {
      const res = await fetchAutenticat(`${API_URL}/TipusAbsencia.php?idEmpresa=${idEmpresa}`);
      const data = await res.json();
      if (data.success) setTipusOptions(data.tipus || []);
    } catch (e) {
      if (!(e instanceof SessioCaducadaError)) console.error("Error carregant Tipus d'absència:", e);
    }
  }, [idEmpresa]);

  const carregarResum = useCallback(async () => {
    if (!idEmpresa) return;
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({ idEmpresa, mostrarActius: mostrarActius ? "1" : "0", desde, finsa });
      const res = await fetchAutenticat(`${API_URL}/GestioLaboralEmpleatsResum.php?${params.toString()}`);
      const data = await res.json();
      if (data.success) {
        setResum(data);
      } else {
        setError(data.message || "No s'ha pogut carregar el resum d'Empleats.");
        setResum(null);
      }
    } catch (e) {
      if (!(e instanceof SessioCaducadaError)) setError("Error de connexió amb el servidor.");
    } finally {
      setLoading(false);
    }
  }, [idEmpresa, mostrarActius, desde, finsa]);

  useEffect(() => {
    carregarTipus();
  }, [carregarTipus]);

  useEffect(() => {
    carregarResum();
  }, [carregarResum]);

  const empleatsFiltrats = useMemo(() => {
    const llista = resum?.empleats || [];
    const text = filtreText.trim().toLowerCase();
    return llista.filter((emp) => {
      if (text) {
        const nomComplet = `${emp.Cognom1 || ""} ${emp.Cognom2 || ""} ${emp.Nom || ""}`.toLowerCase();
        if (!nomComplet.includes(text)) return false;
      }
      if (filtreTipus) {
        const teTipus =
          String(emp.BaixaOberta?.idTipusAbsenciaEmpleats) === filtreTipus ||
          String(emp.BaixaRecent?.idTipusAbsenciaEmpleats) === filtreTipus ||
          emp.AbsenciesAvui.some((a) => String(a.idTipusAbsenciaEmpleats) === filtreTipus) ||
          String(emp.ProperaAbsencia?.idTipusAbsenciaEmpleats) === filtreTipus;
        if (!teTipus) return false;
      }
      if (filtreEstat) {
        const teEstat =
          emp.AbsenciesAvui.some((a) => a.EstatJustificacio === filtreEstat) ||
          emp.ProperaAbsencia?.EstatJustificacio === filtreEstat;
        if (!teEstat) return false;
      }
      return true;
    });
  }, [resum, filtreText, filtreTipus, filtreEstat]);

  const handleSeleccionarEmpleat = (idEmpleats) => {
    if (!idEmpleats) return;
    const emp = (resum?.empleats || []).find((e) => String(e.idEmpleats) === String(idEmpleats));
    if (emp) setEmpleatSeleccionat(emp);
  };

  if (!potAccedir) {
    return (
      <div className="dv2 dv2-page">
        <p className="dv2-empty">No tens permisos per accedir a aquesta pantalla.</p>
      </div>
    );
  }

  if (empleatSeleccionat) {
    return (
      <div className="dv2 dv2-page dv2-page--fill">
        <GestioEmpleat
          empleatProp={empleatSeleccionat}
          empleats={empleatsFiltrats}
          onNavigate={setEmpleatSeleccionat}
          onTornar={() => setEmpleatSeleccionat(null)}
        />
      </div>
    );
  }

  const comptadors = resum?.comptadors;

  return (
    <div className="dv2 dv2-page dv2-page--fill">
      {mostraAjuda && <AjudaGestioLaboralEmpleats onTancar={() => setMostraAjuda(false)} />}

      <div className="dv2-pageHeader">
        <div className="dv2-pageHeader-main">
          <div className="dv2-pageHeader-icon" aria-hidden="true">
            👷
          </div>
          <div>
            <h1 className="dv2-pageHeader-title">Gestió laboral · Empleats</h1>
            <p className="dv2-pageHeader-subtitle">Resum de situació de l'empresa activa.</p>
          </div>
        </div>
        <div className="dv2-pageHeader-actions">
          <button
            type="button"
            className="dv2-btn dv2-btn--icon dv2-btn--ghost"
            title="Com funciona aquesta pantalla"
            aria-label="Ajuda"
            onClick={() => setMostraAjuda(true)}
          >
            ?
          </button>
        </div>
      </div>

      <div className="dv2-listBody">
        <div className="dv2-filterBar">
          <label className="dv2-filter">
            <span className="dv2-filter-label">Empleat</span>
            <select className="dv2-select" value="" onChange={(e) => handleSeleccionarEmpleat(e.target.value)}>
              <option value="">Tots</option>
              {(resum?.empleats || []).map((e) => (
                <option key={e.idEmpleats} value={e.idEmpleats}>
                  {formatNomPersona(e)}
                </option>
              ))}
            </select>
          </label>
          <label className="dv2-filter">
            <span className="dv2-filter-label">Tipus</span>
            <select className="dv2-select" value={filtreTipus} onChange={(ev) => setFiltreTipus(ev.target.value)}>
              <option value="">Tots</option>
              {tipusOptions.map((t) => (
                <option key={t.idTipusAbsenciaEmpleats} value={t.idTipusAbsenciaEmpleats}>
                  {t.Nom}
                </option>
              ))}
            </select>
          </label>
          <label className="dv2-filter">
            <span className="dv2-filter-label">Estat</span>
            <select className="dv2-select" value={filtreEstat} onChange={(ev) => setFiltreEstat(ev.target.value)}>
              <option value="">Tots</option>
              {ESTAT_OPCIONS.map((o) => (
                <option key={o.valor} value={o.valor}>
                  {o.nom}
                </option>
              ))}
            </select>
          </label>
          <label className="dv2-filter">
            <span className="dv2-filter-label">Des de</span>
            <input type="date" className="dv2-input" value={desde} onChange={(ev) => setDesde(ev.target.value)} />
          </label>
          <label className="dv2-filter">
            <span className="dv2-filter-label">Fins a</span>
            <input type="date" className="dv2-input" value={finsa} onChange={(ev) => setFinsa(ev.target.value)} />
          </label>
          <label className="dv2-filter dv2-filter--toggle">
            <span className="dv2-filter-label">Actius</span>
            <span className="dv2-toggleRow">
              <input type="checkbox" checked={mostrarActius} onChange={(ev) => setMostrarActius(ev.target.checked)} />
            </span>
          </label>
          <div className="dv2-filter dv2-filter--grow">
            <span className="dv2-filter-label">Cerca</span>
            <input
              type="text"
              className="dv2-input dv2-input-search"
              placeholder="Cerca per nom..."
              value={filtreText}
              onChange={(e) => setFiltreText(e.target.value)}
            />
          </div>
        </div>

        {loading && <p className="dv2-hint">Carregant resum d'Empleats...</p>}
        {error && <p className="gl-empleats-error">{error}</p>}

        {!loading && !error && (
          <>
            {comptadors && (
              <div className="gl-empleats-comptadors">
                <span className="gl-empleats-comptador">
                  De baixa avui: <strong>{comptadors.baixaAvui}</strong>
                </span>
                {comptadors.perTipusAvui.map((t) => (
                  <span key={t.idTipus} className="gl-empleats-comptador">
                    <span className="gl-empleats-comptador-punt" style={{ backgroundColor: t.Color || "#999999" }} />
                    De {t.Nom.toLowerCase()} avui: <strong>{t.count}</strong>
                  </span>
                ))}
                <span className="gl-empleats-comptador">
                  Pendents de justificar: <strong>{comptadors.pendentJustificar}</strong>
                </span>
              </div>
            )}

            {empleatsFiltrats.length > 0 ? (
              <div className="dv2-tableWrap">
                <table className="dv2-table">
                  <thead>
                    <tr>
                      <th>Empleat</th>
                      <th>Estat avui</th>
                      <th>Baixa</th>
                      <th>Propera absència</th>
                      <th>Pendents justificar</th>
                      <th>Objectiu</th>
                      <th>Treballades</th>
                      <th>Pendent / Excedent</th>
                      <th className="dv2-table-actions"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {empleatsFiltrats.map((emp) => {
                      const nomComplet = formatNomPersona(emp);
                      const hores = emp.Hores;
                      return (
                        <tr key={emp.idEmpleats} onDoubleClick={() => setEmpleatSeleccionat(emp)}>
                          <td>
                            {nomComplet || "—"}
                            {emp.Actiu === 0 && <em> (Inactiu)</em>}
                          </td>
                          <td>
                            {emp.BaixaOberta ? (
                              <span className="gl-empleats-xip" style={{ backgroundColor: emp.BaixaOberta.TipusColor || "#999999" }}>
                                Baixa: {emp.BaixaOberta.TipusNom}
                              </span>
                            ) : emp.AbsenciesAvui.length > 0 ? (
                              emp.AbsenciesAvui.map((a) => (
                                <span key={a.idAbsenciesEmpleats} className="gl-empleats-xip" style={{ backgroundColor: a.TipusColor || "#999999" }}>
                                  {a.TipusNom}
                                </span>
                              ))
                            ) : (
                              "Actiu"
                            )}
                          </td>
                          <td>
                            {emp.BaixaOberta
                              ? `Des de ${formatDate(emp.BaixaOberta.DataInici)}`
                              : emp.BaixaRecent
                              ? `Finalitzada ${formatDate(emp.BaixaRecent.DataFinal)}`
                              : "—"}
                          </td>
                          <td>
                            {emp.ProperaAbsencia
                              ? `${emp.ProperaAbsencia.TipusNom} (${formatDate(emp.ProperaAbsencia.Data)})`
                              : "—"}
                          </td>
                          <td>{emp.PendentsJustificar > 0 ? <strong>{emp.PendentsJustificar}</strong> : 0}</td>
                          <td>{hores ? formatMinuts(hores.horesObjectiuMinuts) : "—"}</td>
                          <td>{hores ? formatMinuts(hores.horesTreballadesMinuts) : "—"}</td>
                          <td>
                            {hores ? (
                              hores.horesExcedentsMinuts > 0 ? (
                                <span className="gl-empleats-hores-excedent">+{formatMinuts(hores.horesExcedentsMinuts)}</span>
                              ) : (
                                <span className="gl-empleats-hores-pendent">{formatMinuts(-hores.horesPendentsMinuts)}</span>
                              )
                            ) : (
                              "—"
                            )}
                          </td>
                          <td className="dv2-table-actions">
                            <button type="button" className="dv2-btn dv2-btn--compact" onClick={() => setEmpleatSeleccionat(emp)}>
                              Gestió
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="dv2-empty">No s'ha trobat cap Empleat amb aquests filtres.</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default GestioLaboralEmpleats;
